/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  getNonNullResolvedInstructionInput,
  type ResolvedInstructionAccount,
} from "@solana/program-client-core";
import {
  findEventAuthorityPda,
  findPoolAuthorityPda,
  findRewardVaultPda,
} from "../pdas/index.js";
import { CP_AMM_PROGRAM_ADDRESS } from "../programs/index.js";

export const WITHDRAW_INELIGIBLE_REWARD_DISCRIMINATOR: ReadonlyUint8Array =
  new Uint8Array([148, 206, 42, 195, 247, 49, 103, 8]);

export function getWithdrawIneligibleRewardDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    WITHDRAW_INELIGIBLE_REWARD_DISCRIMINATOR,
  );
}

export type WithdrawIneligibleRewardInstruction<
  TProgram extends string = typeof CP_AMM_PROGRAM_ADDRESS,
  TAccountPoolAuthority extends string | AccountMeta<string> = string,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountRewardVault extends string | AccountMeta<string> = string,
  TAccountRewardMint extends string | AccountMeta<string> = string,
  TAccountFunderTokenAccount extends string | AccountMeta<string> = string,
  TAccountFunder extends string | AccountMeta<string> = string,
  TAccountTokenProgram extends string | AccountMeta<string> =
    "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA",
  TAccountEventAuthority extends string | AccountMeta<string> = string,
  TAccountProgram extends string | AccountMeta<string> =
    "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPoolAuthority extends string
        ? ReadonlyAccount<TAccountPoolAuthority>
        : TAccountPoolAuthority,
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      TAccountRewardVault extends string
        ? WritableAccount<TAccountRewardVault>
        : TAccountRewardVault,
      TAccountRewardMint extends string
        ? ReadonlyAccount<TAccountRewardMint>
        : TAccountRewardMint,
      TAccountFunderTokenAccount extends string
        ? WritableAccount<TAccountFunderTokenAccount>
        : TAccountFunderTokenAccount,
      TAccountFunder extends string
        ? ReadonlySignerAccount<TAccountFunder> &
            AccountSignerMeta<TAccountFunder>
        : TAccountFunder,
      TAccountTokenProgram extends string
        ? ReadonlyAccount<TAccountTokenProgram>
        : TAccountTokenProgram,
      TAccountEventAuthority extends string
        ? ReadonlyAccount<TAccountEventAuthority>
        : TAccountEventAuthority,
      TAccountProgram extends string
        ? ReadonlyAccount<TAccountProgram>
        : TAccountProgram,
      ...TRemainingAccounts,
    ]
  >;

export type WithdrawIneligibleRewardInstructionData = {
  discriminator: ReadonlyUint8Array;
  rewardIndex: number;
};

export type WithdrawIneligibleRewardInstructionDataArgs = {
  rewardIndex: number;
};

export function getWithdrawIneligibleRewardInstructionDataEncoder(): FixedSizeEncoder<WithdrawIneligibleRewardInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["rewardIndex", getU8Encoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: WITHDRAW_INELIGIBLE_REWARD_DISCRIMINATOR,
    }),
  );
}

export function getWithdrawIneligibleRewardInstructionDataDecoder(): FixedSizeDecoder<WithdrawIneligibleRewardInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["rewardIndex", getU8Decoder()],
  ]);
}

export function getWithdrawIneligibleRewardInstructionDataCodec(): FixedSizeCodec<
  WithdrawIneligibleRewardInstructionDataArgs,
  WithdrawIneligibleRewardInstructionData
> {
  return combineCodec(
    getWithdrawIneligibleRewardInstructionDataEncoder(),
    getWithdrawIneligibleRewardInstructionDataDecoder(),
  );
}

export type WithdrawIneligibleRewardAsyncInput<
  TAccountPoolAuthority extends string = string,
  TAccountPool extends string = string,
  TAccountRewardVault extends string = string,
  TAccountRewardMint extends string = string,
  TAccountFunderTokenAccount extends string = string,
  TAccountFunder extends string = string,
  TAccountTokenProgram extends string = string,
  TAccountEventAuthority extends string = string,
  TAccountProgram extends string = string,
> = {
  poolAuthority?: Address<TAccountPoolAuthority>;
  pool: Address<TAccountPool>;
  rewardVault?: Address<TAccountRewardVault>;
  rewardMint: Address<TAccountRewardMint>;
  funderTokenAccount: Address<TAccountFunderTokenAccount>;
  funder: TransactionSigner<TAccountFunder>;
  tokenProgram?: Address<TAccountTokenProgram>;
  eventAuthority?: Address<TAccountEventAuthority>;
  program?: Address<TAccountProgram>;
  rewardIndex: WithdrawIneligibleRewardInstructionDataArgs["rewardIndex"];
};

export async function getWithdrawIneligibleRewardInstructionAsync<
  TAccountPoolAuthority extends string,
  TAccountPool extends string,
  TAccountRewardVault extends string,
  TAccountRewardMint extends string,
  TAccountFunderTokenAccount extends string,
  TAccountFunder extends string,
  TAccountTokenProgram extends string,
  TAccountEventAuthority extends string,
  TAccountProgram extends string,
  TProgramAddress extends Address = typeof CP_AMM_PROGRAM_ADDRESS,
>(
  input: WithdrawIneligibleRewardAsyncInput<
    TAccountPoolAuthority,
    TAccountPool,
    TAccountRewardVault,
    TAccountRewardMint,
    TAccountFunderTokenAccount,
    TAccountFunder,
    TAccountTokenProgram,
    TAccountEventAuthority,
    TAccountProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  WithdrawIneligibleRewardInstruction<
    TProgramAddress,
    TAccountPoolAuthority,
    TAccountPool,
    TAccountRewardVault,
    TAccountRewardMint,
    TAccountFunderTokenAccount,
    TAccountFunder,
    TAccountTokenProgram,
    TAccountEventAuthority,
    TAccountProgram
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? CP_AMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    poolAuthority: { value: input.poolAuthority ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: true },
    rewardVault: { value: input.rewardVault ?? null, isWritable: true },
    rewardMint: { value: input.rewardMint ?? null, isWritable: false },
    funderTokenAccount: {
      value: input.funderTokenAccount ?? null,
      isWritable: true,
    },
    funder: { value: input.funder ?? null, isWritable: false },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
    eventAuthority: { value: input.eventAuthority ?? null, isWritable: false },
    program: { value: input.program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.poolAuthority.value) {
    accounts.poolAuthority.value = await findPoolAuthorityPda();
  }
  if (!accounts.rewardVault.value) {
    accounts.rewardVault.value = await findRewardVaultPda({
      pool: getAddressFromResolvedInstructionAccount(
        "pool",
        accounts.pool.value,
      ),
      rewardIndex: getNonNullResolvedInstructionInput(
        "rewardIndex",
        args.rewardIndex,
      ),
    });
  }
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }
  if (!accounts.eventAuthority.value) {
    accounts.eventAuthority.value = await findEventAuthorityPda();
  }
  if (!accounts.program.value) {
    accounts.program.value =
      "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG" as Address<"cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("poolAuthority", accounts.poolAuthority),
      getAccountMeta("pool", accounts.pool),
      getAccountMeta("rewardVault", accounts.rewardVault),
      getAccountMeta("rewardMint", accounts.rewardMint),
      getAccountMeta("funderTokenAccount", accounts.funderTokenAccount),
      getAccountMeta("funder", accounts.funder),
      getAccountMeta("tokenProgram", accounts.tokenProgram),
      getAccountMeta("eventAuthority", accounts.eventAuthority),
      getAccountMeta("program", accounts.program),
    ],
    data: getWithdrawIneligibleRewardInstructionDataEncoder().encode(
      args as WithdrawIneligibleRewardInstructionDataArgs,
    ),
    programAddress,
  } as WithdrawIneligibleRewardInstruction<
    TProgramAddress,
    TAccountPoolAuthority,
    TAccountPool,
    TAccountRewardVault,
    TAccountRewardMint,
    TAccountFunderTokenAccount,
    TAccountFunder,
    TAccountTokenProgram,
    TAccountEventAuthority,
    TAccountProgram
  >);
}

export type WithdrawIneligibleRewardInput<
  TAccountPoolAuthority extends string = string,
  TAccountPool extends string = string,
  TAccountRewardVault extends string = string,
  TAccountRewardMint extends string = string,
  TAccountFunderTokenAccount extends string = string,
  TAccountFunder extends string = string,
  TAccountTokenProgram extends string = string,
  TAccountEventAuthority extends string = string,
  TAccountProgram extends string = string,
> = {
  poolAuthority: Address<TAccountPoolAuthority>;
  pool: Address<TAccountPool>;
  rewardVault: Address<TAccountRewardVault>;
  rewardMint: Address<TAccountRewardMint>;
  funderTokenAccount: Address<TAccountFunderTokenAccount>;
  funder: TransactionSigner<TAccountFunder>;
  tokenProgram?: Address<TAccountTokenProgram>;
  eventAuthority: Address<TAccountEventAuthority>;
  program?: Address<TAccountProgram>;
  rewardIndex: WithdrawIneligibleRewardInstructionDataArgs["rewardIndex"];
};

export function getWithdrawIneligibleRewardInstruction<
  TAccountPoolAuthority extends string,
  TAccountPool extends string,
  TAccountRewardVault extends string,
  TAccountRewardMint extends string,
  TAccountFunderTokenAccount extends string,
  TAccountFunder extends string,
  TAccountTokenProgram extends string,
  TAccountEventAuthority extends string,
  TAccountProgram extends string,
  TProgramAddress extends Address = typeof CP_AMM_PROGRAM_ADDRESS,
>(
  input: WithdrawIneligibleRewardInput<
    TAccountPoolAuthority,
    TAccountPool,
    TAccountRewardVault,
    TAccountRewardMint,
    TAccountFunderTokenAccount,
    TAccountFunder,
    TAccountTokenProgram,
    TAccountEventAuthority,
    TAccountProgram
  >,
  config?: { programAddress?: TProgramAddress },
): WithdrawIneligibleRewardInstruction<
  TProgramAddress,
  TAccountPoolAuthority,
  TAccountPool,
  TAccountRewardVault,
  TAccountRewardMint,
  TAccountFunderTokenAccount,
  TAccountFunder,
  TAccountTokenProgram,
  TAccountEventAuthority,
  TAccountProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? CP_AMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    poolAuthority: { value: input.poolAuthority ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: true },
    rewardVault: { value: input.rewardVault ?? null, isWritable: true },
    rewardMint: { value: input.rewardMint ?? null, isWritable: false },
    funderTokenAccount: {
      value: input.funderTokenAccount ?? null,
      isWritable: true,
    },
    funder: { value: input.funder ?? null, isWritable: false },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
    eventAuthority: { value: input.eventAuthority ?? null, isWritable: false },
    program: { value: input.program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }
  if (!accounts.program.value) {
    accounts.program.value =
      "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG" as Address<"cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("poolAuthority", accounts.poolAuthority),
      getAccountMeta("pool", accounts.pool),
      getAccountMeta("rewardVault", accounts.rewardVault),
      getAccountMeta("rewardMint", accounts.rewardMint),
      getAccountMeta("funderTokenAccount", accounts.funderTokenAccount),
      getAccountMeta("funder", accounts.funder),
      getAccountMeta("tokenProgram", accounts.tokenProgram),
      getAccountMeta("eventAuthority", accounts.eventAuthority),
      getAccountMeta("program", accounts.program),
    ],
    data: getWithdrawIneligibleRewardInstructionDataEncoder().encode(
      args as WithdrawIneligibleRewardInstructionDataArgs,
    ),
    programAddress,
  } as WithdrawIneligibleRewardInstruction<
    TProgramAddress,
    TAccountPoolAuthority,
    TAccountPool,
    TAccountRewardVault,
    TAccountRewardMint,
    TAccountFunderTokenAccount,
    TAccountFunder,
    TAccountTokenProgram,
    TAccountEventAuthority,
    TAccountProgram
  >);
}

export type ParsedWithdrawIneligibleRewardInstruction<
  TProgram extends string = typeof CP_AMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    poolAuthority: TAccountMetas[0];
    pool: TAccountMetas[1];
    rewardVault: TAccountMetas[2];
    rewardMint: TAccountMetas[3];
    funderTokenAccount: TAccountMetas[4];
    funder: TAccountMetas[5];
    tokenProgram: TAccountMetas[6];
    eventAuthority: TAccountMetas[7];
    program: TAccountMetas[8];
  };
  data: WithdrawIneligibleRewardInstructionData;
};

export function parseWithdrawIneligibleRewardInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedWithdrawIneligibleRewardInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 9) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 9,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      poolAuthority: getNextAccount(),
      pool: getNextAccount(),
      rewardVault: getNextAccount(),
      rewardMint: getNextAccount(),
      funderTokenAccount: getNextAccount(),
      funder: getNextAccount(),
      tokenProgram: getNextAccount(),
      eventAuthority: getNextAccount(),
      program: getNextAccount(),
    },
    data: getWithdrawIneligibleRewardInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
